/// <reference types="applepayjs" />

import { useBehavior, getGlobalVariables, useTriggerAction, useUpdateWidgetValue } from '@libs/hooks';
import { ActionActionTypeEnum, TCommonDataObj, TWidget, TWidgetProps } from '@libs/types';
import React, { Activity, useCallback, useEffect, useRef } from 'react';
import { TApplePay } from '../../types/data';
import { TButtonOption } from '../../types/options';
import { addScript, removeScript } from '../../utils/helpers';
import { getInlineStyles } from '../../utils/styles';
import { ApplePayButton } from './ApplePay';
import styles from './styles/payButtonWrapper.module.css';

// @ts-expect-error
const isApplePayAvailable = () => window.ApplePaySession && ApplePaySession.canMakePayments();

/**
 * For this widget we should set the Global variable in .env file with URL of chat script
 *  example: NX_PUBLIC_GLOBAL_applePaySDK='https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js'
 */
export const ButtonApplePay = ({ widget }: TWidgetProps<TApplePay, TButtonOption>) => {
    const { applePaySDK } = getGlobalVariables('applePaySDK');
    const isApplePayAdded = useRef(isApplePayAvailable());
    const buttonRef = useRef(null);

    const { value, countryCode, supportedNetworks } = widget.data;

    const { isHidden } = useBehavior(widget as TWidget<TApplePay>);
    const { triggerActionsByNameAndType } = useTriggerAction(widget as TWidget<TApplePay>);
    const { updateWidgetValue } = useUpdateWidgetValue(widget);

    const callFailureAction = useCallback(
        (err: unknown) => {
            updateWidgetValue(err as TCommonDataObj);
            triggerActionsByNameAndType(ActionActionTypeEnum.onClick, 'onFailure').catch((e) => {
                console.log('Cannot call failure action: ', e);
            });
        },
        [triggerActionsByNameAndType, updateWidgetValue],
    );

    const handleApplePay = useCallback(async () => {
        if (!isApplePayAvailable()) {
            return;
        }

        try {
            const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
                countryCode: countryCode,
                currencyCode: value.curIso,
                supportedNetworks: supportedNetworks,
                merchantCapabilities: ['supports3DS'],
                total: {
                    label: '',
                    amount: value.amount.toString(),
                },
            };

            const session = new ApplePaySession(3, paymentRequest);

            session.onvalidatemerchant = async (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
                updateWidgetValue(event as unknown as TCommonDataObj);
                try {
                    const merchantSession = await triggerActionsByNameAndType(ActionActionTypeEnum.onClick, 'onMerchantValidation').then(
                        (w) => w?.data?.authorizeMerchant,
                    );
                    session.completeMerchantValidation(merchantSession);
                } catch (err) {
                    callFailureAction(err);
                    session.abort();
                }
            };

            session.onpaymentauthorized = (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
                session.completePayment(ApplePaySession.STATUS_SUCCESS);
                updateWidgetValue(event as unknown as TCommonDataObj);
                triggerActionsByNameAndType(ActionActionTypeEnum.onClick, 'onSuccess').catch((err) => console.log('Cannot send success action: ', err));
            };

            session.oncancel = (event: ApplePayJS.Event) => {
                callFailureAction(event);
            };

            session.begin();
        } catch (err) {
            callFailureAction(err);
        }
    }, [callFailureAction, countryCode, supportedNetworks, triggerActionsByNameAndType, updateWidgetValue, value]);

    useEffect(() => {
        if (!isApplePayAdded.current) {
            addScript(applePaySDK);
            isApplePayAdded.current = true;
        }
        const buttonElement = buttonRef.current;
        if (buttonElement !== null) {
            // @ts-expect-error
            buttonElement.addEventListener('click', handleApplePay);
        }
        return () => {
            removeScript(applePaySDK);
            if (buttonElement !== null) {
                // @ts-expect-error
                buttonElement.removeEventListener('click', handleApplePay);
            }
        };
    }, [applePaySDK, handleApplePay]);

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <div className={styles.payButtonWrapper} style={getInlineStyles({ margin: widget.options?.margin })}>
                <ApplePayButton
                    style={getInlineStyles({
                        variables: {
                            'apple-pay-button-width': '212px',
                            'apple-pay-button-height': '56px',
                            'apple-pay-button-border-radius': '23px',
                            'apple-pay-button-padding': '0px 0px',
                            'apple-pay-button-box-sizing': 'border-box',
                        },
                    })}
                    ref={buttonRef}
                    buttonStyle="black"
                    type="pay"
                />
            </div>
        </Activity>
    );
};
